"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/icon";
import { Mail, Phone, ChevronDown, ChevronLeft } from "lucide-react";
import { NavigationItem } from "@/types/header";
import type { Category } from "@/types/category";

type MenuBarProps = {
    categories: Category[];
    navigation: NavigationItem[];
};

const topBarItems = [
    {
        type: "contact",
        icon: "phone",
        value: "۰۲۱-۴۴۴۴۶۰۱۲",
        href: "tel:+982144446012",
        hideOnMobile: true,
    },
    {
        type: "contact",
        icon: "mail",
        value: "sale@ittelecom.ir",
        href: "mailto:sale@ittelecom.ir",
        hideOnMobile: false,
    },
];

const categoryColors = [
    "#edf3ff",
    "#fff3f5",
    "#edfbf7",
    "#fff6ec",
    "#e6f9f5",
    "#edf1f6",
    "#f3e6ff",
];

const getChildren = (categories: Category[], parentId: number) =>
    categories
        .filter((cat) => Number(cat.parent_id) === parentId)
        .sort((a, b) => a.order! - b.order!);

type CategoryFlyoutItemProps = {
    categories: Category[];
    category: Category;
    hrefBase: string;
};

function CategoryFlyoutItem({
                                categories,
                                category,
                                hrefBase,
                            }: CategoryFlyoutItemProps) {
    const children = getChildren(categories, Number(category.id));
    const href = `${hrefBase}/${category.slug}`;
    const isLabel = category.style === "label";

    return (
        <>
            <Link
                href={href}
                prefetch={true}
                className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-100 ${
                    isLabel ? "font-bold text-lg text-blue-600" : "text-gray-700 pr-6"
                }`}
            >
                {isLabel && (
                    <span className="w-1 h-5 bg-blue-600 rounded-lg"></span>
                )}
                <span>{category.title}</span>
                {isLabel && children.length > 0 && (
                    <ChevronLeft className="w-4 h-4" />
                )}
            </Link>
            {children.length > 0 && (
                <div className="flex flex-col mb-5">
                    {children.map((child) => (
                        <CategoryFlyoutItem
                            key={Number(child.id)}
                            categories={categories}
                            category={child}
                            hrefBase={href}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

const MenuBar = ({ categories = [], navigation }: MenuBarProps) => {
    const [openTopId, setOpenTopId] = useState<number | null>(null);

    return (
        <nav className="hidden lg:flex items-center gap-5 relative">
            {navigation.map((item) => {
                const isCategory = item.name === "دسته‌بندی‌ها";
                const IconComponent = item.icon;
                return (
                    <div
                        key={item.name}
                        className={`relative group ${item.action === "1" ? "" : "py-0"}`}
                    >
                        <Link
                            href={item.href}
                            prefetch={true}
                            className={
                                item.action === "1"
                                    ? `p-2 flex text-white rounded-t-lg bg-blue-600 items-center dark:bg-blue-900/40`
                                    : `flex text-gray-700 hover:text-[#252360] font-medium py-2 relative items-center dark:hover:text-white`
                            }
                        >
                            <IconComponent className="h-4 w-4 ml-1 flex-shrink-0" />
                            <span>{item.name}</span>
                            {item.action === "1" && (
                                <ChevronDown className="w-4 h-4 mr-3" />
                            )}
                            <span className="absolute bottom-0 right-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full dark:bg-white"></span>
                        </Link>

                        {isCategory && (
                            <div className="hidden group-hover:block absolute right-0 top-full bg-white shadow-lg min-w-[240px] z-50 py-2 rounded-b-lg animate-slide-up">
                                <ul>
                                    {getChildren(categories, 0).map(
                                        (parent, index) => {
                                            const children = getChildren(
                                                categories,
                                                Number(parent.id)
                                            );
                                            const hrefBase = `/${process.env.NEXT_PUBLIC_SHOP_CATEGORY}/${parent.slug}`;
                                            const isOpen =
                                                openTopId === Number(parent.id);

                                            return (
                                                <li
                                                    key={Number(parent.id)}
                                                    className="relative"
                                                    onMouseEnter={() =>
                                                        setOpenTopId(
                                                            Number(parent.id)
                                                        )
                                                    }
                                                    onMouseLeave={() =>
                                                        setOpenTopId(null)
                                                    }
                                                >
                                                    <Link
                                                        href={hrefBase}
                                                        className="flex justify-between items-center px-3 py-1 hover:bg-gray-100 last:rounded-b-lg last:hover:rounded-b-lg"
                                                        prefetch={true}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <span
                                                                aria-hidden="true"
                                                                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                                                                style={{
                                                                    backgroundColor:
                                                                        categoryColors[
                                                                        index %
                                                                        categoryColors.length
                                                                            ],
                                                                }}
                                                            >
                                                                {parent.primary_image && (
                                                                    <Icon
                                                                        class_name="w-6 h-6 filter-black"
                                                                        alt={`icon-menubar-${parent.primary_image}`}
                                                                        data={`${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/menu/${parent.primary_image}`}
                                                                    />
                                                                )}
                                                            </span>
                                                            {parent.title}
                                                        </span>
                                                        {children.length >
                                                            0 && (
                                                                <ChevronLeft className="w-4 h-4 mr-3" />
                                                            )}
                                                    </Link>

                                                    {children.length > 0 &&
                                                        isOpen && (
                                                            <div className="absolute right-full top-0 bg-white shadow-lg min-w-[300px] rounded-lg z-50 py-2 max-h-[80vh] overflow-y-auto">
                                                                {children.map(
                                                                    (
                                                                        child
                                                                    ) => (
                                                                        <CategoryFlyoutItem
                                                                            key={Number(
                                                                                child.id
                                                                            )}
                                                                            categories={
                                                                                categories
                                                                            }
                                                                            category={
                                                                                child
                                                                            }
                                                                            hrefBase={
                                                                                hrefBase
                                                                            }
                                                                        />
                                                                    )
                                                                )}
                                                            </div>
                                                        )}
                                                </li>
                                            );
                                        }
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="flex items-center gap-4 absolute left-0 top-0 py-1">
                {topBarItems.map((item, index) => {
                    if (item.type === "contact") {
                        const IconComponent =
                            item.icon === "phone" ? Phone : Mail;
                        return (
                            <Link
                                key={index}
                                href={item.href}
                                target="_blank"
                                className="flex items-center hover:bg-gray-200 rounded-lg p-2 py-1"
                            >
                                <span>{item.value}</span>
                                <IconComponent className="h-4 w-4 mr-2" />
                            </Link>
                        );
                    }
                    return null;
                })}
            </div>
        </nav>
    );
};

export default MenuBar;
